import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api from '../lib/api';
import {
  Activity, AlertTriangle, Siren, Percent, Timer, Loader2, ImageOff, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { UrgencyBadge, EmergencyFlagBadge } from '../components/UrgencyBadge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from 'recharts';

const LEVEL_COLORS = {
  CRITICAL: '#EF4444',
  HIGH: '#FB923C',
  MODERATE: '#FACC15',
  LOW: '#4ADE80',
  NONE: '#4ADE80',
  Unknown: '#64748B',
};

const PIE_COLORS = ['#22D3EE', '#2DD4BF', '#38BDF8', '#818CF8', '#F472B6', '#FACC15'];

const chartTooltipStyle = {
  backgroundColor: 'hsl(210, 18%, 10%)',
  border: '1px solid hsl(210, 14%, 20%)',
  borderRadius: '8px',
  fontSize: '12px',
  color: '#E2E8F0',
};

/**
 * Measures its own width via ResizeObserver and renders the chart with
 * explicit pixel dimensions (avoids recharts ResponsiveContainer
 * width/height -1 warnings on mount).
 */
const ChartBox = ({ height, children }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height }}>
      {width > 0 ? children(width, height) : null}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="bg-[hsl(210,18%,9%)] border-border" data-testid={testId}>
    <CardContent className="py-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xl font-bold leading-none">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/stats')
      .then((res) => setStats(res.data))
      .catch((err) => console.error('Stats error:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <ImageOff size={48} className="text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t('dashboard.no_data')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/')}>
          {t('nav.upload')}
        </Button>
      </div>
    );
  }

  const levelData = (stats.by_level || []).map((d) => ({
    ...d,
    fill: LEVEL_COLORS[d.name] || LEVEL_COLORS.Unknown,
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="dashboard-page"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{t('dashboard.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          icon={Activity}
          label={t('dashboard.total')}
          value={stats.total}
          accent="bg-cyan-500/15 text-cyan-400"
          testId="stat-total"
        />
        <StatCard
          icon={AlertTriangle}
          label={t('dashboard.emergencies')}
          value={stats.emergencies}
          accent="bg-red-500/15 text-red-400"
          testId="stat-emergencies"
        />
        <StatCard
          icon={Siren}
          label={t('dashboard.critical')}
          value={stats.critical}
          accent="bg-red-500/15 text-red-400"
          testId="stat-critical"
        />
        <StatCard
          icon={Percent}
          label={t('dashboard.emergency_rate')}
          value={`${stats.emergency_rate}%`}
          accent="bg-orange-500/15 text-orange-400"
          testId="stat-rate"
        />
        <StatCard
          icon={Timer}
          label={t('dashboard.avg_latency')}
          value={stats.avg_latency_ms ? `${(stats.avg_latency_ms / 1000).toFixed(1)}s` : '—'}
          accent="bg-teal-500/15 text-teal-400"
          testId="stat-latency"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 lg:col-span-6 bg-[hsl(210,18%,9%)] border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('dashboard.by_level')}</CardTitle>
          </CardHeader>
          <CardContent style={{ width: '100%' }}>
            <ChartBox height={240}>
              {(w, h) => (
              <BarChart width={w} height={h} data={levelData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 14%, 16%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ReTooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {levelData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
              )}
            </ChartBox>
          </CardContent>
        </Card>

        <Card className="col-span-12 lg:col-span-6 bg-[hsl(210,18%,9%)] border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('dashboard.by_modality')}</CardTitle>
          </CardHeader>
          <CardContent style={{ width: '100%' }}>
            <ChartBox height={240}>
              {(w, h) => (
              <PieChart width={w} height={h}>
                <Pie
                  data={stats.by_modality || []}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  label={({ name, count }) => `${name} (${count})`}
                  labelLine={false}
                  fontSize={10}
                >
                  {(stats.by_modality || []).map((entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip contentStyle={chartTooltipStyle} />
              </PieChart>
              )}
            </ChartBox>
          </CardContent>
        </Card>

        <Card className="col-span-12 lg:col-span-7 bg-[hsl(210,18%,9%)] border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('dashboard.by_day')}</CardTitle>
          </CardHeader>
          <CardContent style={{ width: '100%' }}>
            <ChartBox height={220}>
              {(w, h) => (
              <AreaChart width={w} height={h} data={stats.by_day || []} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22D3EE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 14%, 16%)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ReTooltip contentStyle={chartTooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#22D3EE" strokeWidth={2} fill="url(#volumeGradient)" />
              </AreaChart>
              )}
            </ChartBox>
          </CardContent>
        </Card>

        {/* Recent critical */}
        <Card className="col-span-12 lg:col-span-5 bg-[hsl(210,18%,9%)] border-border" data-testid="recent-critical-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Siren size={15} className="text-red-400" />
              {t('dashboard.recent_critical')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(stats.recent_critical || []).length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-4 text-center">—</p>
            ) : (
              stats.recent_critical.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/analysis/${a.id}`)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-[hsl(210,14%,12%)] hover:bg-[hsl(210,14%,15%)] transition-colors duration-150 text-left"
                  data-testid={`recent-critical-${a.id}`}
                >
                  <UrgencyBadge level={a.emergency_level} size="sm" showLabel={false} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{a.modality_guess} — {a.region}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                      {a.user_name ? ` · ${a.user_name}` : ''}
                    </p>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};

export default DashboardPage;
